import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, Paper, Stack, Typography, Chip, IconButton, Divider, Tooltip, Link } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import GoogleMap from "google-map-react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { connectorStatusWhenOffline } from "src/utils/connectorStatus";
import { queryKeys } from "src/react-query/queryKeys";
import { fetchWithError } from "src/react-query/fetchWithError";
import { fEnergy } from "src/utils/formatNumber";
import { VehicleLink } from "./DetailLinks";

// A reusable map of every charger. Clicking a marker opens a detail panel that
// shows the charger's name, status, power and address — and, when the team has the
// Vehicle Charging section on, the vehicles charging at it right now.

// Same query (and key) as the Vehicle Charging card's Charging Now tab, so the two
// share one cached, auto-refreshing request.
const ACTIVE_REFRESH_MS = 30000;
const vehicleLabel = (row) =>
  row.vehicleId == null ? "Unknown vehicle" : row.registrationNumber || `Vehicle ${row.vehicleId}`;
const fSoc = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(0)}%`);

const isOnline = (charger) => charger?.status === "Active";
// Only an online charger can be charging: an offline one's stored connector
// status is stale (it shows as Offline).
const isCharging = (charger) =>
  isOnline(charger) &&
  (Array.isArray(charger?.connectors) ? charger.connectors : []).some(
    (c) => String(c?.status || "").toUpperCase() === "CHARGING"
  );
// Three states: In Use > Online (available) > Offline.
const chargerStateMeta = (charger, theme) => {
  if (isCharging(charger)) return { label: "In Use", color: theme.palette.info.main };
  if (isOnline(charger)) return { label: "Online", color: theme.palette.success.main };
  return { label: "Offline", color: theme.palette.error.main };
};

// Per-connector power, e.g. [{ label: "Connector 1", power: "30 kW", type: "CCS" }].
const connectorPowers = (charger) => {
  const connectors = Array.isArray(charger?.connectors) ? charger.connectors : [];
  // Always number connectors sequentially (1, 2, …) — ignore the stored name/id.
  return connectors.map((c, i) => {
    const kw = parseFloat(c?.kw);
    return {
      label: `Connector ${i + 1}`,
      power: !isNaN(kw) && kw > 0 ? `${kw} kW` : "—",
      status: (isOnline(charger) ? c?.status : connectorStatusWhenOffline(c?.status)) || null,
      errorCode: c?.errorCode || null,
    };
  });
};

const connectorStatusColor = (status, theme) => {
  const CONNECTOR_STATUS_COLORS = {
    AVAILABLE: theme.palette.success.main,
    CHARGING: theme.palette.info.main,
    PREPARING: theme.palette.warning.main,
    FINISHING: theme.palette.warning.main,
    SUSPENDEDEV: theme.palette.warning.main,
    RESERVED: theme.palette.info.light,
    UNAVAILABLE: theme.palette.text.secondary,
    FAULTED: theme.palette.error.main,
    OFFLINE: theme.palette.error.main,
    MAINTENANCE: theme.palette.violet.main,
  };
  return CONNECTOR_STATUS_COLORS[String(status || "").toUpperCase()] || theme.palette.text.secondary;
};

// Inline SVG map pin (no external image dependency) with the amik "∧" mark,
// colored by charger state.
const pinDataUri = (color) =>
  "data:image/svg+xml;charset=UTF-8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 30 40">` +
      `<path d="M15 0C6.72 0 0 6.72 0 15c0 10.3 13.05 23.8 13.6 24.37a1.95 1.95 0 0 0 2.8 0C16.95 38.8 30 25.3 30 15 30 6.72 23.28 0 15 0z" fill="${color}" stroke="#ffffff" stroke-width="1.6"/>` +
      `<path d="M7.5 19 L15 9 L22.5 19" fill="none" stroke="#ffffff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>` +
      `<path d="M11 19 L15 13.5 L19 19" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" opacity="0.85"/></svg>`
  );

// Marker color: green = available, blue = in use, red = offline/faulted (same
// colors as the Chargers status tiles).
const markerColor = (charger, theme) => {
  if (charger?.isFaulted) return theme.palette.error.main;
  if (isCharging(charger)) return theme.palette.info.main; // in use
  if (isOnline(charger)) return theme.palette.success.main; // available
  return theme.palette.error.main; // offline
};

// Map legend — must stay in sync with markerColor above.
const mapLegend = (theme) => [
  { color: theme.palette.success.main, label: "Available" },
  { color: theme.palette.info.main, label: "In Use" },
  { color: theme.palette.error.main, label: "Offline / Faulted" },
];

const chargerName = (charger) =>
  charger?.chargepoint_name ||
  charger?.name ||
  charger?.chargepoint_id ||
  charger?.charge_box_id ||
  charger?.serial_number ||
  "Charging Station";

const chargerId = (charger) =>
  charger?.chargepoint_id || charger?.charge_box_id || charger?.serial_number || "—";

// Short label shown by default: only city and state.
const chargerCityState = (charger) =>
  [charger?.city, charger?.state].filter(Boolean).join(", ") || "Location not available";

// Full address revealed on hover.
const chargerFullAddress = (charger) =>
  [charger?.street, charger?.houseNumber, charger?.city, charger?.state, charger?.zipCode, charger?.country]
    .filter(Boolean)
    .join(", ") || "Address not available";

const hasCoords = (charger) => {
  const lat = Number(charger?.locationLatitude);
  const lng = Number(charger?.locationLongitude);
  return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
};

const ChargerLocationsMap = ({ chargers, height = 420, teamId = null, showVehicles = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const returnTo = `${routerLocation.pathname}${routerLocation.search}`;
  const [map, setMap] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [selected, setSelected] = useState(null);
  const markersRef = useRef([]);

  const data = Array.isArray(chargers) ? chargers : [];

  const { data: activeSessions } = useQuery(
    queryKeys.vehicleChargingActive(teamId, undefined),
    fetchWithError,
    {
      enabled: Boolean(showVehicles && teamId),
      refetchInterval: ACTIVE_REFRESH_MS,
      select: (r) => r?.data ?? [],
    }
  );
  // Live sessions grouped by charger (pk, else charge box id).
  const sessionsByCharger = useMemo(() => {
    const byCharger = new Map();
    if (!showVehicles) return byCharger;
    (activeSessions || []).forEach((row) => {
      const id = row.chargerPk ?? row.chargerId;
      if (id == null) return;
      const key = String(id);
      if (!byCharger.has(key)) byCharger.set(key, []);
      byCharger.get(key).push(row);
    });
    byCharger.forEach((rows) => rows.sort((a, b) => Number(a.connectorId) - Number(b.connectorId)));
    return byCharger;
  }, [activeSessions, showVehicles]);
  const sessionsFor = useCallback(
    (charger) => {
      const pk = charger?.chargepoint_pk;
      const boxId = charger?.chargepoint_id ?? charger?.charge_box_id;
      return (
        (pk != null && sessionsByCharger.get(String(pk))) ||
        (boxId != null && sessionsByCharger.get(String(boxId))) ||
        []
      );
    },
    [sessionsByCharger]
  );

  const drawMarkers = useCallback(
    (mapInstance) => {
      if (!mapInstance || !window.google) return;
      const { LatLng, Marker, Size, Point } = window.google.maps;

      // Clear existing markers
      markersRef.current.forEach((m) => m?.setMap && m.setMap(null));
      markersRef.current = [];

      const bounds = new window.google.maps.LatLngBounds();
      let plotted = 0;

      data.forEach((charger) => {
        if (!hasCoords(charger)) return;
        const lat = Number(charger.locationLatitude);
        const lng = Number(charger.locationLongitude);
        const latLng = new LatLng(lat, lng);

        const marker = new Marker({
          position: latLng,
          map: mapInstance,
          title: chargerName(charger),
          icon: {
            url: pinDataUri(markerColor(charger, theme)),
            size: new Size(28, 38),
            origin: new Point(0, 0),
            anchor: new Point(14, 38),
            scaledSize: new Size(28, 38),
          },
        });

        marker.charger = charger;
        marker.addListener("click", () => setSelected(charger));
        markersRef.current.push(marker);
        bounds.extend(latLng);
        plotted += 1;
      });

      if (plotted > 1) {
        mapInstance.fitBounds(bounds);
      } else if (plotted === 1) {
        mapInstance.setCenter(bounds.getCenter());
        mapInstance.setZoom(10);
      }
    },
    [data, theme]
  );

  const handleApiLoaded = useCallback(({ map: mapInstance }) => {
    setMap(mapInstance);
    setMapReady(true);
  }, []);

  useEffect(() => {
    if (map && mapReady) drawMarkers(map);
  }, [map, mapReady, drawMarkers]);

  // Hover title names the vehicles charging. Updated in place so the 30s refresh
  // doesn't redraw the pins (which would refit the map and lose the user's zoom).
  useEffect(() => {
    markersRef.current.forEach((marker) => {
      const charging = sessionsFor(marker.charger);
      marker.setTitle(
        charging.length
          ? `${chargerName(marker.charger)} — charging ${charging.map(vehicleLabel).join(", ")}`
          : chargerName(marker.charger)
      );
    });
  }, [sessionsFor, map, mapReady, drawMarkers]);

  // Cleanup markers on unmount
  useEffect(
    () => () => {
      markersRef.current.forEach((m) => m?.setMap && m.setMap(null));
      markersRef.current = [];
    },
    []
  );

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <Box
        sx={{
          height,
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          border: "1px solid rgba(0,0,0,0.08)",
          background: "linear-gradient(135deg, #f5f5f5 0%, #e3f2fd 100%)",
        }}
      >
        <GoogleMap
          bootstrapURLKeys={{
            key: "AIzaSyB5VfSNqyhjYLoiXpI94yp5H2TPBgOVitY",
            libraries: ["places"],
          }}
          defaultCenter={{ lat: 20.5937, lng: 78.9629 }}
          defaultZoom={4}
          onGoogleApiLoaded={handleApiLoaded}
          yesIWantToUseGoogleMapApiInternals
          options={{
            disableDefaultUI: false,
            zoomControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
            gestureHandling: "greedy",
          }}
        />
      </Box>

      {/* Legend: what each marker color means */}
      <Paper
        elevation={3}
        sx={{
          position: "absolute",
          bottom: 12,
          left: 12,
          px: 1.25,
          py: 0.75,
          borderRadius: 1.5,
          bgcolor: "rgba(255,255,255,0.95)",
          zIndex: 4,
        }}
      >
        <Stack spacing={0.5}>
          {mapLegend(theme).map((l) => (
            <Stack key={l.label} direction="row" alignItems="center" spacing={0.75}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor: l.color,
                  flexShrink: 0,
                }}
              />
              <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", whiteSpace: "nowrap" }}>
                {l.label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Paper>

      {selected && (
        <Paper
          elevation={8}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            width: { xs: "calc(100% - 32px)", sm: 300 },
            // Stay inside the map (16px gap top and bottom); long details scroll.
            maxHeight: `calc(${typeof height === "number" ? `${height}px` : height} - 32px)`,
            overflowY: "auto",
            p: 2,
            borderRadius: 2,
            zIndex: 5,
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
            <Box sx={{ pr: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                {chargerName(selected)}
              </Typography>
              {selected?.chargepoint_pk != null ? (
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() =>
                    navigate(`/charger/${selected.chargepoint_pk}`, { state: { returnTo } })
                  }
                  sx={{ fontSize: "0.8rem", fontWeight: 600, textAlign: "left", cursor: "pointer" }}
                >
                  {chargerId(selected)}
                </Link>
              ) : (
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {chargerId(selected)}
                </Typography>
              )}
            </Box>
            <IconButton size="small" onClick={() => setSelected(null)} aria-label="close details">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Stack direction="row" spacing={0.75} sx={{ mt: 0.5, flexWrap: "wrap" }}>
            {(() => {
              const meta = chargerStateMeta(selected, theme);
              return (
                <Chip
                  size="small"
                  label={meta.label}
                  sx={{ fontWeight: 600, color: meta.color, bgcolor: `${meta.color}18` }}
                />
              );
            })()}
            {selected?.isFaulted && (
              <Chip
                size="small"
                label={selected?.lastError?.errorCode || "Faulted"}
                sx={{ fontWeight: 600, color: theme.palette.error.main, bgcolor: `${theme.palette.error.main}18` }}
              />
            )}
          </Stack>
          <Divider sx={{ my: 1.5 }} />
          <Stack spacing={1.25}>
            <Box>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Connector Power
              </Typography>
              {connectorPowers(selected).length > 0 ? (
                <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                  {connectorPowers(selected).map((c, i) => (
                    <Stack
                      key={`${c.label}-${i}`}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: connectorStatusColor(c.status, theme) }} />
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          {c.label}
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {c.status && (
                          <Typography variant="caption" sx={{ color: connectorStatusColor(c.status, theme), fontWeight: 600 }}>
                            {c.status}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {c.power}
                        </Typography>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Not available
                </Typography>
              )}
            </Box>
            {sessionsFor(selected).length > 0 && (
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Vehicles Charging
                </Typography>
                <Stack spacing={0.75} sx={{ mt: 0.25 }}>
                  {sessionsFor(selected).map((row) => (
                    <Box key={row.transactionId}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: row.vehicleId == null ? "text.secondary" : undefined }}
                      >
                        <VehicleLink vehicleId={row.vehicleId}>{vehicleLabel(row)}</VehicleLink>
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        {[
                          `Connector ${row.connectorId}`,
                          row.batterySlot ? `Battery ${row.batterySlot}` : null,
                          row.startSoC != null || row.currentSoC != null
                            ? `SoC ${fSoc(row.startSoC)} → ${fSoc(row.currentSoC)}`
                            : null,
                          row.energyKwh != null ? fEnergy(row.energyKwh) : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
            {selected?.chargePointVendor && (
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Vendor
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {selected.chargePointVendor}
                </Typography>
              </Box>
            )}
            {selected?.charger_type && (
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Type
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {selected.charger_type}
                </Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Location
              </Typography>
              <Tooltip title={chargerFullAddress(selected)} arrow placement="top">
                <Typography
                  variant="body2"
                  sx={{ cursor: "help", textDecoration: "underline dotted", textUnderlineOffset: 2 }}
                >
                  {chargerCityState(selected)}
                </Typography>
              </Tooltip>
            </Box>
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

ChargerLocationsMap.propTypes = {
  chargers: PropTypes.array,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  teamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showVehicles: PropTypes.bool,
};

export default ChargerLocationsMap;
